/**
 * Auth state  -  kept in its own Zustand store, separate from the main app store.
 *
 * Why separate: the main store holds user-content (purchases, scenarios) that
 * gets re-loaded from cloud on each login. Auth state has a different lifecycle
 * (initialized once at app boot, cleared on sign out). Keeping them separate
 * avoids cross-contamination on sign-in/sign-out flows.
 *
 * Patch AM: added role-awareness. After sign-in resolves, the store fetches
 * the user's role from app_state.role (one extra SELECT against the same
 * row already touched by sync.ts). Components like the Admin tab gate
 * visibility on this value. Promotion/demotion goes through /api/admin
 * (server-side super-admin check, service_role for the actual UPDATE).
 *
 * The role fetch is intentionally NOT routed through sync.ts because sync
 * owns the state jsonb column and its persistence semantics; role is a
 * column sibling with a totally different lifecycle (set by admin via a
 * separate write path, read by user once on sign-in). Keeping the read
 * here means sync.ts and realtime.ts stay untouched.
 *
 * Patch AN: added thin helpers for the cohort_members and plans ops added
 * to /api/admin.ts plus the upload helper for /api/upload-plan.ts. These
 * are stateless wrappers (no store state) so the admin-panel and my-plan
 * components can fetch their own data on mount without a zustand subscription
 * pattern. Putting them on useAuthStore keeps the bearer-token plumbing
 * centralized; if this list keeps growing it'd be worth extracting to a
 * dedicated store, but four ops doesn't justify it yet.
 */

import { create } from 'zustand';
import type { Session, User } from '@supabase/supabase-js';
import { supabase, cloudEnabled } from '../io/supabase/client.js';
import { getCurrentSubdomain } from '../io/tenant.js';

export type AuthStatus =
  | 'loading'      // initial  -  waiting for getSession()
  | 'signed-out'   // no session
  | 'signed-in'    // session active
  | 'sending'      // OTP request in flight
  | 'sent'         // OTP sent, awaiting code entry
  | 'verifying';   // user submitted code, verifying with Supabase

export type UserRole = 'standard' | 'admin';

// ---- Patch AN: types for cohort/plan operations ----

export interface CohortMember {
  id: string;
  cohortNumber: number;
  memberEmail: string;
  addedAt: string;
}

/** A plan as seen by the admin who uploaded it (their listAdminPlans view). */
export interface AdminPlan {
  id: string;
  recipientEmail: string;
  cohortNumber: number | null;
  filename: string;
  contentType: string | null;
  sizeBytes: number | null;
  note: string | null;
  uploadedAt: string;
}

/** A plan as seen by the recipient client (their listMyPlans view). */
export interface MyPlan {
  id: string;
  uploadedByEmail: string | null;
  cohortNumber: number | null;
  filename: string;
  contentType: string | null;
  sizeBytes: number | null;
  note: string | null;
  uploadedAt: string;
}

export interface UploadPlanArgs {
  recipientEmail: string;
  cohortNumber: number | null;
  file: File;
  note?: string;
}

interface AuthState {
  status: AuthStatus;
  user: User | null;
  session: Session | null;
  error: string | null;
  pendingEmail: string | null;
  /** Patch AM: the signed-in user's role on app_state.role. null until
   *  fetched (or when signed-out). Components read this to gate admin-only UI. */
  role: UserRole | null;

  init: () => Promise<void>;
  signInWithEmail: (email: string) => Promise<void>;
  verifyOtp: (token: string) => Promise<void>;
  signOut: () => Promise<void>;
  reset: () => void;

  // ---- Patch AM: super-admin operations ----
  lookupUserByEmail: (email: string) => Promise<{ userId: string; email: string }>;
  setUserRole: (email: string, role: UserRole) => Promise<void>;
  listAdmins: () => Promise<Array<{ userId: string; email: string }>>;

  // ---- Patch AN: cohort + plan operations ----
  /** Admin: add a member to one of the caller's cohorts. Idempotent on
   *  (admin, cohort, email). */
  addCohortMember: (cohortNumber: number, memberEmail: string) => Promise<CohortMember>;
  /** Admin: remove a cohort_members row by id. Scoped to caller's own rows
   *  server-side, so passing another admin's UUID is a no-op (returns success
   *  but doesn't actually delete). */
  removeCohortMember: (cohortMemberId: string) => Promise<void>;
  /** Admin: list the caller's cohort members, optionally filtered to one
   *  cohort number. */
  listCohortMembers: (cohortNumber?: number) => Promise<CohortMember[]>;
  /** Admin: list plans the caller has uploaded, optionally filtered to one
   *  cohort number. */
  listAdminPlans: (cohortNumber?: number) => Promise<AdminPlan[]>;
  /** Admin: delete a plan + its storage object. Scoped to caller's own
   *  uploads; will 404 on plans uploaded by anyone else. */
  deletePlan: (planId: string) => Promise<void>;
  /** Admin: upload a plan PDF for a recipient. Rejects non-PDF and files
   *  larger than 3MB. Returns the new plan's id and metadata. */
  uploadPlan: (args: UploadPlanArgs) => Promise<AdminPlan>;
  /** Any user: list plans where caller is the recipient (matched by user_id
   *  OR email). */
  listMyPlans: () => Promise<MyPlan[]>;
  /** Any user with access (recipient or uploader): get a 5-minute signed
   *  download URL for the plan's PDF file. */
  getPlanDownloadUrl: (planId: string) => Promise<{ url: string; filename: string; contentType: string | null }>;

  // ---- Patch AS: admin client-view ----
  /** Admin: load a cohort member's app_state.state column for a read-only
   *  view-as session. Server enforces the cohort-roster check; super-admin
   *  bypasses it. Returns the encoded state shape (same as cloud loads use),
   *  which the caller hands to store.enterViewAs. */
  loadClientState: (memberEmail: string) => Promise<{ state: unknown; userId: string; email: string }>;
}

let initialized = false;

async function fetchRole(userId: string): Promise<UserRole> {
  if (!supabase) return 'standard';
  try {
    const { data, error } = await supabase
      .from('app_state')
      .select('role')
      .eq('user_id', userId)
      .maybeSingle();
    if (error || !data) return 'standard';
    return data.role === 'admin' ? 'admin' : 'standard';
  } catch {
    return 'standard';
  }
}

async function callAdminApi<T>(token: string, body: unknown): Promise<T> {
  const res = await fetch('/api/admin', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    // Non-JSON body. Fall through; we'll throw a generic HTTP error below.
  }
  if (!res.ok) {
    const msg = payload && typeof payload === 'object' && 'error' in payload
      ? String((payload as { error: unknown }).error)
      : `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return payload as T;
}

/** Patch AN: read a File into base64 (no data: prefix) for /api/upload-plan.
 *  We use FileReader.readAsDataURL because it handles encoding for us; the
 *  prefix split drops the "data:application/pdf;base64," part since the
 *  endpoint wants raw base64. */
async function fileToBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('FileReader returned non-string result'));
        return;
      }
      const commaIdx = result.indexOf(',');
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('File read failed'));
    reader.readAsDataURL(file);
  });
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: cloudEnabled() ? 'loading' : 'signed-out',
  user: null,
  session: null,
  error: null,
  pendingEmail: null,
  role: null,

  init: async () => {
    if (initialized || !supabase) {
      // No cloud, run in offline mode (treat as signed-out, app should still work).
      if (!supabase) set({ status: 'signed-out' });
      return;
    }
    initialized = true;

    // getSession reads from localStorage when the access token is fresh (instant).
    // Expired tokens trigger a silent network refresh which can stall for seconds
    // on a cold iOS connection, leaving the AuthGate black div visible indefinitely.
    // Race against a 4s timeout; fall through to signed-out if the refresh hangs.
    // onAuthStateChange fires automatically once the refresh eventually completes,
    // signing the user back in without them re-entering their email.
    const session = await Promise.race([
      supabase.auth.getSession().then(r => r.data.session),
      new Promise<null>(resolve => setTimeout(() => resolve(null), 4_000)),
    ]);
    if (session) {
      set({ status: 'signed-in', user: session.user, session, error: null });
      const role = await fetchRole(session.user.id);
      set({ role });
    } else {
      set({ status: 'signed-out' });
    }

    supabase.auth.onAuthStateChange(async (event, sess) => {
      if (event === 'SIGNED_OUT') {
        set({ status: 'signed-out', user: null, session: null, error: null, pendingEmail: null, role: null });
      } else if (sess) {
        set({ status: 'signed-in', user: sess.user, session: sess, error: null, pendingEmail: null });
        const role = await fetchRole(sess.user.id);
        set({ role });
      }
    });
  },

  signInWithEmail: async (email: string) => {
    if (!supabase) {
      set({ error: 'Cloud sync is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY.' });
      return;
    }
    if (!email || !email.includes('@')) {
      set({ error: 'Please enter a valid email address.' });
      return;
    }
    set({ status: 'sending', error: null });
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: window.location.origin,
      },
    });
    if (error) {
      set({ status: 'signed-out', error: error.message, pendingEmail: null });
    } else {
      set({ status: 'sent', pendingEmail: email });
    }
  },

  verifyOtp: async (token: string) => {
    if (!supabase) return;
    const email = get().pendingEmail;
    if (!email) {
      set({ error: 'Please request a new code.' });
      return;
    }
    const cleanToken = token.replace(/\s+/g, '').trim();
    // Supabase's email OTP length depends on project config  -  defaults to 6
    // digits but can be set to 8 (or higher in some templates). Accept any
    // pure-digit token between 6 and 10 chars.
    if (!/^\d{6,10}$/.test(cleanToken)) {
      set({ error: 'Enter the numeric code from the email.' });
      return;
    }
    set({ status: 'verifying', error: null });

    // 20s timeout so verifyOtp can never hang indefinitely (e.g. network drop).
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('Verification timed out. Please try again.')), 20_000);
    });

    try {
      const { data, error } = await Promise.race([
        supabase.auth.verifyOtp({ email, token: cleanToken, type: 'email' }),
        timeoutPromise,
      ]);
      if (error) {
        set({ status: 'sent', error: error.message });
      } else {
        // verifyOtp can return data.session=null in some Supabase configs
        // (e.g. when localStorage quota fails in iOS private mode and Supabase
        // cannot persist the session before returning). Try getSession()
        // immediately, then poll with brief delays so the user is never silently
        // stuck at 'verifying'  -  they always get either signed-in or a clear error.
        let session = data.session ?? (await supabase.auth.getSession()).data.session;
        if (!session) {
          for (let i = 0; i < 5; i++) {
            await new Promise<void>(resolve => setTimeout(resolve, 400));
            const { data: { session: polled } } = await supabase.auth.getSession();
            if (polled) { session = polled; break; }
          }
        }
        if (session) {
          set({ status: 'signed-in', user: session.user, session, error: null, pendingEmail: null });
        } else {
          // All polling attempts failed. onAuthStateChange (registered in init)
          // may still fire if the session propagates  -  but do not leave the user
          // silently stuck at 'verifying'. Return them to 'sent' with guidance.
          set({ status: 'sent', error: 'Verification succeeded but sign-in stalled. Refresh the page and try again.' });
        }
      }
    } catch (e) {
      set({ status: 'sent', error: e instanceof Error ? e.message : 'Verification failed. Please try again.' });
    } finally {
      if (timeoutId !== null) clearTimeout(timeoutId);
    }
  },

  signOut: async () => {
    if (!supabase) return;
    // Clear local state first so the UI responds instantly regardless of network.
    // Server-side revocation is best-effort: on a stale TCP connection the request
    // hangs indefinitely, and the JWT expires naturally within an hour anyway.
    set({ status: 'signed-out', user: null, session: null, error: null, pendingEmail: null, role: null });
    void supabase.auth.signOut().catch(() => undefined);
  },

  reset: () => {
    set({ status: get().session ? 'signed-in' : 'signed-out', error: null, pendingEmail: null });
  },

  // ---- Patch AM: super-admin operations ----
  lookupUserByEmail: async (email: string) => {
    const token = get().session?.access_token;
    if (!token) throw new Error('Not signed in');
    return callAdminApi<{ userId: string; email: string }>(token, {
      op: 'lookupUserByEmail',
      email,
    });
  },

  setUserRole: async (email: string, role: UserRole) => {
    const token = get().session?.access_token;
    if (!token) throw new Error('Not signed in');
    await callAdminApi<{ success: boolean }>(token, { op: 'setRole', email, role });
  },

  listAdmins: async () => {
    const token = get().session?.access_token;
    if (!token) throw new Error('Not signed in');
    const result = await callAdminApi<{ admins: Array<{ userId: string; email: string }> }>(token, {
      op: 'listAdmins',
    });
    return result.admins;
  },

  // ---- Patch AN: cohort + plan operations ----
  addCohortMember: async (cohortNumber, memberEmail) => {
    const token = get().session?.access_token;
    if (!token) throw new Error('Not signed in');
    return callAdminApi<CohortMember>(token, {
      op: 'addCohortMember',
      tenant: getCurrentSubdomain() ?? 'rajat',
      cohortNumber,
      memberEmail,
    });
  },

  removeCohortMember: async (cohortMemberId) => {
    const token = get().session?.access_token;
    if (!token) throw new Error('Not signed in');
    await callAdminApi<{ success: boolean }>(token, {
      op: 'removeCohortMember',
      tenant: getCurrentSubdomain() ?? 'rajat',
      cohortMemberId,
    });
  },

  listCohortMembers: async (cohortNumber) => {
    const token = get().session?.access_token;
    if (!token) throw new Error('Not signed in');
    const result = await callAdminApi<{ members: CohortMember[] }>(token, {
      op: 'listCohortMembers',
      tenant: getCurrentSubdomain() ?? 'rajat',
      ...(typeof cohortNumber === 'number' ? { cohortNumber } : {}),
    });
    return result.members;
  },

  listAdminPlans: async (cohortNumber) => {
    const token = get().session?.access_token;
    if (!token) throw new Error('Not signed in');
    const result = await callAdminApi<{ plans: AdminPlan[] }>(token, {
      op: 'listAdminPlans',
      tenant: getCurrentSubdomain() ?? 'rajat',
      ...(typeof cohortNumber === 'number' ? { cohortNumber } : {}),
    });
    return result.plans;
  },

  deletePlan: async (planId) => {
    const token = get().session?.access_token;
    if (!token) throw new Error('Not signed in');
    await callAdminApi<{ success: boolean }>(token, {
      op: 'deletePlan',
      tenant: getCurrentSubdomain() ?? 'rajat',
      planId,
    });
  },

  uploadPlan: async ({ recipientEmail, cohortNumber, file, note }) => {
    const token = get().session?.access_token;
    if (!token) throw new Error('Not signed in');
    if (file.type !== 'application/pdf') {
      throw new Error('Only PDF files are supported');
    }
    if (file.size > 3 * 1024 * 1024) {
      throw new Error('File too large; max 3MB');
    }
    const fileBase64 = await fileToBase64(file);
    const res = await fetch('/api/upload-plan', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        recipientEmail,
        cohortNumber,
        tenant: getCurrentSubdomain() ?? 'rajat',
        filename: file.name,
        contentType: file.type,
        fileBase64,
        note: note ?? null,
      }),
    });
    let payload: unknown = null;
    try { payload = await res.json(); } catch { /* fallthrough */ }
    if (!res.ok) {
      const msg = payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `HTTP ${res.status}`;
      throw new Error(msg);
    }
    const ok = payload as { plan: AdminPlan };
    return ok.plan;
  },

  listMyPlans: async () => {
    const token = get().session?.access_token;
    if (!token) throw new Error('Not signed in');
    const result = await callAdminApi<{ plans: MyPlan[] }>(token, { op: 'listMyPlans' });
    return result.plans;
  },

  getPlanDownloadUrl: async (planId) => {
    const token = get().session?.access_token;
    if (!token) throw new Error('Not signed in');
    return callAdminApi<{ url: string; filename: string; contentType: string | null }>(token, {
      op: 'getPlanDownloadUrl',
      planId,
    });
  },

  loadClientState: async (memberEmail) => {
    const token = get().session?.access_token;
    if (!token) throw new Error('Not signed in');
    return callAdminApi<{ state: unknown; userId: string; email: string }>(token, {
      op: 'loadClientState',
      tenant: getCurrentSubdomain() ?? 'rajat',
      memberEmail,
    });
  },
}));
